import { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";
import { getDeviceId, setPinEmail, clearPinEmail } from "@/lib/pp_device";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const raw = localStorage.getItem("pp_user");
    return raw ? JSON.parse(raw) : null;
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("pp_token");
    if (token) {
      api.get("/auth/me")
        .then((r) => { setUser(r.data); localStorage.setItem("pp_user", JSON.stringify(r.data)); })
        .catch(() => { setUser(null); localStorage.removeItem("pp_token"); localStorage.removeItem("pp_user"); })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const _persist = (token, u) => {
    localStorage.setItem("pp_token", token);
    localStorage.setItem("pp_user", JSON.stringify(u));
    setUser(u);
  };

  const login = async (email, password) => {
    // Cold-start + Cloudflare 502 safety net. Production sometimes returns
    // transient 502s (Cloudflare → origin timeout) — retry up to 3 times
    // with widening timeouts. Each retry uses fresh axios state so a stale
    // connection from the previous attempt can't bite us.
    const attempts = [
      { timeout: 25000, backoffMs: 0 },
      { timeout: 35000, backoffMs: 1500 },
      { timeout: 45000, backoffMs: 4000 },
    ];
    let lastErr;
    for (let i = 0; i < attempts.length; i++) {
      const { timeout, backoffMs } = attempts[i];
      if (backoffMs > 0) await new Promise((res) => setTimeout(res, backoffMs));
      try {
        const r = await api.post("/auth/login", { email, password }, { timeout });
        _persist(r.data.token, r.data.user);
        return r.data.user;
      } catch (err) {
        lastErr = err;
        const status = err?.response?.status;
        const transient = !status || status === 502 || status === 503 || status === 504
          || err?.code === "ERR_NETWORK" || err?.code === "ECONNABORTED"
          || /timeout/i.test(err?.message || "");
        if (!transient) throw err;
        if (i < attempts.length - 1) {
          // eslint-disable-next-line no-console
          console.warn(`[login] attempt ${i + 1} failed (${err?.code || status || "?"}), retrying…`);
        }
      }
    }
    throw lastErr;
  };

  const loginWithPin = async (email, pin) => {
    const r = await api.post("/auth/pin/login", { email, pin, device_id: getDeviceId() });
    _persist(r.data.token, r.data.user);
    return r.data.user;
  };

  const register = async (data) => {
    const r = await api.post("/auth/register", data);
    _persist(r.data.token, r.data.user);
    return r.data.user;
  };

  const setupPin = async (pin, device_label = "") => {
    await api.post("/auth/pin/set", { pin, device_id: getDeviceId(), device_label });
    if (user?.email) setPinEmail(user.email);
    return true;
  };

  const removePin = async () => {
    try { await api.delete("/auth/pin", { params: { device_id: getDeviceId() } }); } catch {}
    clearPinEmail();
  };

  const checkPinStatus = async () => {
    try {
      const r = await api.get("/auth/pin/status", { params: { device_id: getDeviceId() } });
      return r.data;
    } catch { return { has_any_pin: false, has_on_device: false }; }
  };

  const logout = () => {
    localStorage.removeItem("pp_token");
    localStorage.removeItem("pp_user");
    // Don't leak previous user's coach thread on a shared device
    localStorage.removeItem("pp_coach_sid");
    // We deliberately KEEP pp_pin_email + pp_device_id so PIN sign-in still works after logout.
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, loginWithPin, register, logout, setupPin, removePin, checkPinStatus }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
